package uk.co.nickthecoder.kyd.appearance

import uk.co.nickthecoder.glok.backend.PartialTexture
import uk.co.nickthecoder.glok.backend.backend
import uk.co.nickthecoder.glok.property.boilerplate.colorProperty
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.kyd.Actor
import uk.co.nickthecoder.kyd.Vector2Property
import uk.co.nickthecoder.kyd.util.Vector2
import uk.co.nickthecoder.kyd.vector2Property
import kotlin.math.max
import kotlin.math.min

/**
 * Draws a [NinePatch] to fill a rectangular region defined by 2 opposite corners of a rectangle
 * ([corner0] and [corner2]).
 *
 * NOTE. Changing the details of the [NinePatch] after creating a [NinePatchAppearance] has no effect.
 * You can think of [NinePatch] being data which helps create a [NinePatchAppearance], but once it is created,
 * the [NinePatch] is no longer needed.
 */
class NinePatchAppearance(

    ninePatch: NinePatch

) : StretchableAppearance, TintedAppearance {

    override val tintProperty by colorProperty(Color.WHITE)
    override var tint by tintProperty

    override val resourcePath = ninePatch.path()

    private val margins = ninePatch.margins

    /**
     * The positions of two opposite corners in [CoordinateSystem.local] coordinates.
     * Initially, these are determined by the [NinePatch]'s data such that none of the patches are scaled.
     * i.e. the drawn image will be exactly the same size as the original image (with no scaling).
     */
    override val corner0Property by vector2Property(Vector2(- ninePatch.pin.x, ninePatch.pin.y))
    override var corner0 by corner0Property

    override val corner2Property by vector2Property(
        Vector2(ninePatch.graphicWidth - ninePatch.pin.x, ninePatch.pin.y - ninePatch.graphicHeight)
    )
    override var corner2 by corner2Property

    private val patches: Array<Array<PartialTexture>>

    init {
        val tex = ninePatch.graphic.texture
        val mar = ninePatch.margins
        val left = ninePatch.graphicLeft.toFloat()
        val top = ninePatch.graphicTop.toFloat()
        val cw = ninePatch.graphicWidth - mar.left - mar.right
        val ch = ninePatch.graphicHeight - mar.top - mar.bottom

        // TODO If any of the margins are zero, use `null` to indicate this part doesn't need to be drawn.
        patches = arrayOf(
            arrayOf(
                PartialTexture(tex, left, top, mar.left, mar.top),
                PartialTexture(tex, left + mar.left, top, cw, mar.top),
                PartialTexture(tex, left + mar.left + cw, top, mar.right, mar.top)
            ),
            arrayOf(
                PartialTexture(tex, left, top + mar.top, mar.left, ch),
                PartialTexture(tex, left + mar.left, top + mar.top, cw, ch),
                PartialTexture(tex, left + mar.left + cw, top + mar.top, mar.right, ch)
            ),
            arrayOf(
                PartialTexture(tex, left, top + mar.top + ch, mar.left, mar.bottom),
                PartialTexture(tex, left + mar.left, top + mar.top + ch, cw, mar.bottom),
                PartialTexture(tex, left + mar.left + cw, top + mar.top + ch, mar.right, mar.bottom)
            )
        )
    }

    override fun draw(modelMatrix: Matrix) {

        // TODO We could cache `xs` and `ys`, updating them when either corner changes.
        // For scenery, the corners never move.
        val left = min(corner0.x, corner2.x)
        val right = max(corner0.x, corner2.x)
        val top = max(corner0.y, corner2.y)
        val bottom = min(corner0.y, corner2.y)

        val x1 = left + margins.left
        val x2 = right - margins.right
        val y1 = top - margins.top
        val y2 = bottom + margins.bottom

        val xs = arrayOf(left, x1, x2, right)
        val ys = arrayOf(top, y1, y2, bottom)

        for (iy in 0..2) {
            val y = ys[iy]
            val h = ys[iy + 1] - y

            for (ix in 0..2) {
                //if (ix == 1 || iy == 1) continue

                val x = xs[ix]
                val w = xs[ix + 1] - x

                val patch = patches[iy][ix]

                // NOTE, PartialTexture was a handy way to define the patches, but it doesn't provide
                // methods to draw to arbitrary destination sizes. So we call backend ourselves.
                // TODO Use batches, to speed things up slightly. (There are only 9 in a batch. Not much).
                backend.drawTintedTexture(
                    patch.texture,
                    tint,
                    patch.srcX, patch.srcY, patch.imageWidth, patch.imageHeight,
                    x, y, w, h,
                    modelMatrix
                )

            }
        }
    }

}
